import { Flex, Text, toast } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';

/**
 * Toast variant types supported by the toast utility.
 */
export type TToastVariant = 'success' | 'error' | 'warning' | 'info';

/**
 * Props for the custom toast content component.
 */
interface IToastContentProps {
  /** Title text displayed in bold */
  title: string;
  /** Description text displayed below title in lighter color */
  description?: string;
}

/**
 * Custom toast content component matching Figma design.
 *
 * Displays an icon on the left, followed by a bold title and optional description.
 * Use this component to display toast with title and description.
 * @param props - Component props
 * @returns Toast content JSX
 */
function ToastContent({ title, description }: IToastContentProps) {
  return (
    <Flex direction="column" gap={0.5} className="min-w-0 flex-1">
      <Text level="base" weight="semibold">
        {title}
      </Text>
      {description && (
        <Text level="small" color="muted">
          {description}
        </Text>
      )}
    </Flex>
  );
}

/**
 * Shows a toast notification with title, optional description, and variant-specific styling.
 *
 * Matches the Figma design with icon on left, bold title, and lighter description.
 *
 * @param title - Title text displayed in bold
 * @param description - Optional description text displayed below title
 * @param variant - Toast variant (success, error, warning, info)
 * @param options - Additional toast options (duration, id, etc.)
 *
 * @example
 * showToast('Success', 'Your changes have been saved', 'success');
 * showToast('Error', 'Something went wrong', 'error');
 */
export function showToast(
  title: string,
  description?: string,
  variant: TToastVariant = 'info',
  options?: Parameters<typeof toast.success>[1],
) {
  const content = <ToastContent title={title} description={description} />;

  // Call the appropriate toast method based on variant
  switch (variant) {
    case 'success':
      return toast.success(content, options);
    case 'error':
      return toast.error(content, options);
    case 'warning':
      return toast.warning(content, options);
    case 'info':
      return toast.info(content, options);
    default:
      return toast.info(content, options);
  }
}

const AccessDeniedToastContent = () => {
  const { t: tCommon } = useTranslation('common');

  return (
    <ToastContent
      title={tCommon('common.accessDenied.title')}
      description={tCommon('common.accessDenied.description')}
    />
  );
};

export const showAccessDeniedToast = (
  options?: Parameters<typeof toast.error>[1],
) => {
  toast.error(<AccessDeniedToastContent />, options);
};
